/**
 * Thin, fail-safe bridge between the shop's cart data (prices in EUR) and the
 * GA4/GTM dataLayer helpers in gtm.ts (item price in EUR, but the total-based
 * events expect CENTS). Every call is wrapped so a tracking error can never
 * break the shop.
 */
import type { CartItem } from '../stores/cart'
import {
  gtmAddToCart,
  gtmAddPaymentInfo,
  gtmBeginCheckout,
  gtmGenerateLead,
  gtmLogin,
  gtmPurchase,
  gtmRemoveFromCart,
  gtmSearch,
  gtmSelectItem,
  gtmSignUp,
  gtmViewCart,
  gtmViewItem,
  gtmViewItemList,
  type GtmItem,
} from './gtm'

// Minimal product shape the shop pages map to (price in EUR).
export interface ShopProductLite {
  id: number | string
  name: string
  slug?: string
  kind?: string
  variant?: string
  price: number
}

function toLite(p: ShopProductLite): GtmItem {
  return {
    item_id: p.id,
    item_name: p.name,
    item_slug: p.slug,
    item_category: p.kind,
    item_variant: p.variant,
    price: p.price || 0,
    quantity: 1,
    currency: 'EUR',
  }
}

const cents = (eur: number): number => Math.round((eur || 0) * 100)

function toItem(ci: CartItem): GtmItem {
  return {
    item_id: ci.product_id,
    item_name: ci.name,
    item_slug: ci.slug,
    item_category: ci.kind,
    item_variant: ci.billing_cycle,
    price: ci.total_price, // per-unit EUR
    quantity: ci.quantity,
    currency: ci.currency || 'EUR',
  }
}

export function trackAddToCart(ci: CartItem): void {
  try {
    gtmAddToCart(toItem(ci))
  } catch {
    /* never break the shop on tracking */
  }
}

export function trackViewItemList(listName: string, products: ShopProductLite[]): void {
  try {
    gtmViewItemList(listName, products.map(toLite))
  } catch {
    /* noop */
  }
}

export function trackViewItem(p: ShopProductLite): void {
  try {
    gtmViewItem(toLite(p))
  } catch {
    /* noop */
  }
}

export function trackViewCart(items: CartItem[], grossEur: number, opts?: { netEur?: number; taxEur?: number }): void {
  try {
    gtmViewCart(items.map(toItem), {
      total_gross: cents(grossEur),
      subtotal_net: opts?.netEur !== undefined ? cents(opts.netEur) : undefined,
      tax: opts?.taxEur !== undefined ? cents(opts.taxEur) : undefined,
    })
  } catch {
    /* noop */
  }
}

export function trackRemoveFromCart(item: CartItem): void {
  try {
    gtmRemoveFromCart(toItem(item))
  } catch {
    /* noop */
  }
}

export function trackAddPaymentInfo(paymentType: string, items: CartItem[], grossEur: number): void {
  try {
    gtmAddPaymentInfo(paymentType, items.map(toItem), { total_gross: cents(grossEur) })
  } catch {
    /* noop */
  }
}

export function trackSearch(term: string, resultsCount?: number): void {
  try {
    gtmSearch(term, resultsCount)
  } catch {
    /* noop */
  }
}

export function trackSelectItem(listName: string, p: ShopProductLite): void {
  try {
    gtmSelectItem(listName, toLite(p))
  } catch {
    /* noop */
  }
}

export function trackSignUp(method: string, customerId?: number): void {
  try {
    gtmSignUp(method, customerId)
  } catch {
    /* noop */
  }
}

export function trackLogin(method: string, customerId?: number): void {
  try {
    gtmLogin(method, customerId)
  } catch {
    /* noop */
  }
}

export function trackGenerateLead(valueEur?: number): void {
  try {
    gtmGenerateLead(valueEur !== undefined ? Math.round(valueEur * 100) : undefined)
  } catch {
    /* noop */
  }
}

export function trackBeginCheckout(
  items: CartItem[],
  grossEur: number,
  opts?: { taxEur?: number; coupon?: string },
): void {
  try {
    gtmBeginCheckout(items.map(toItem), {
      total_gross: cents(grossEur),
      tax: opts?.taxEur !== undefined ? cents(opts.taxEur) : undefined,
      coupon: opts?.coupon || undefined,
    })
  } catch {
    /* noop */
  }
}

export function trackPurchase(
  orderNo: string,
  items: CartItem[],
  totals: { grossEur: number; netEur: number; taxEur: number; coupon?: string; payment?: string; invoiceNo?: string },
): void {
  try {
    gtmPurchase({
      order_number: orderNo,
      invoice_number: totals.invoiceNo,
      subtotal_net: cents(totals.netEur),
      tax: cents(totals.taxEur),
      total_gross: cents(totals.grossEur),
      currency: 'EUR',
      coupon: totals.coupon || undefined,
      payment_method: totals.payment,
      items: items.map(toItem),
    })
  } catch {
    /* noop */
  }
}
